import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Forms from 'components/Forms/Forms'
import { Container } from 'components/Container/Container'
import { Menu } from '../../components/Menu/Menu'
import { Banner } from '../../components/Banner/Banner'
import {
  createUser,
  createVendorCommunityUser,
  fetchUserData,
  getHealthSystem,
  updateUser
} from './api'
import { fetchHealthSystemsList } from 'redux/store/healthSystemsList/slice'
import { getUser } from 'redux/store/user/getters'
import useRouter from 'hooks/useRouter'
import { USER_PROFILE_BREADCRUMBS } from './constants'
import { setLoading } from 'redux/store/common/slice'
import { updateUserProfile } from '../../features/UserProfile/api'
import { useCRUDUserContext } from './Provider/CRUDUserProvider'
import { notification } from '../../components/Notification'
import { PROFILE_TABS } from './constants'
import { ROLES } from '../../features/Permission'
import { TUserForm } from 'components/Forms/forms.user.d'
import { TSelectedUser } from './types'
import './styles.scss'
import { UserHeader } from './UserHeader'
import { UserContractCategories } from '../../features/UserContractCategories/UserContractCategories'
import { useAppDispatch } from '../../redux/createStore'
import { HospitalsTableContainer } from '../../features/UserProfile/HospitalsTable/HospitalsTableContainer'
import { routes } from '../../router'
import { MY_COMMUNITY_KEYS } from '../../features/MyCommunity/constants'
import { LocationStateWithTab } from '../../constants/types'
import { BUTTON_TYPES } from 'components/Button'
import { UserArchivePopup } from './UserArchivePopup'
import { ReviewAndApproveCategoriesPopup } from './ApproveContractCategoriesPopups/ReviewAndApproveCategoriesPopup'
import { ActivateWithoutCategoriesPopup } from './ApproveContractCategoriesPopups/ActivateWithoutCategoriesPopup'
import { useApproveContractCategories } from './hooks/useApproveContractCategories'
import { USER_STATUSES } from 'constants/common'

export const CRUDUser = () => {
  const {
    state: {
      userData,
      totalNumber,
      userForm,
      isOwnProfile,
      canViewFormContractCategoriesInput,
      canViewUserContractCategories,
      canSeeExtendedViewMode,
      canViewFormHospitalList,
      canViewCommunityContractCategories,
      canCreateCommunityContractCategories,
      canUpdateCommunityContractCategories,
      canViewUserContractCategoriesSettings,
      canApprovePendingUsers,
      profileUserRole
    },
    actions: { setUserData }
  } = useCRUDUserContext()

  const [isActive, setIsActive] = useState(false)
  const { id } = useParams()
  const dispatch = useAppDispatch()
  const user = useSelector(getUser)
  const [isShow, setIsShow] = useState(false)
  const [isArchiveOpen, setIsArchiveOpen] = useState(false)
  const { query } = useRouter()
  const fromCommunity = query.fromCommunity
  const navigate = useNavigate()
  const location = useLocation()
  const { redirectTab } = (location.state || {}) as LocationStateWithTab
  const vendorId = query.vendorId

  const communityVendorId = useMemo(() => {
    return !user.vendor && fromCommunity && vendorId ? vendorId : undefined
  }, [user.vendor, fromCommunity, vendorId])

  const getUserData = useCallback(() => {
    if (id && id !== 'new') {
      dispatch(setLoading(true))
      return fetchUserData(id).then((data) => {
        if (data?.data?.community) {
          getHealthSystem(
            data?.data?.community,
            data?.data?.health_system
          ).then(() => {
            setUserData({
              ...data?.data,
              ...{
                health_system: data?.data?.health_system,
                avatar:
                  data?.data?.avatar?.file || data?.data?.avatar_logo?.file
              }
            })
          })
        }
        setUserData({
          ...data?.data,
          ...{
            avatar: data?.data?.avatar?.file || data?.data?.avatar_logo?.file
          }
        })
        setIsShow(true)
      })
    }

    if (query?.hsUser || query?.hsHosUser) {
      const data = {
        health_system: query?.hsUser || query?.hsHosUser
      }
      if (location.search.includes('&role=rca')) {
        data['role'] = 'community_rca'
      }
      if (location.search.includes('&role=community_hospital_user')) {
        data['role'] = 'community_hospital_user'
      }
      setUserData({ ...data })
    }

    setIsShow(true)
    dispatch(setLoading(false))
    return Promise.resolve()
  }, [id, query, dispatch, setUserData, location.search])

  useEffect(() => {
    getUserData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const refreshUserData = useCallback(() => {
    return getUserData()
  }, [getUserData])

  const {
    isPendingUserNeedsToReviewCategories,
    isCurrentUserCanReviewCategories,
    onArchiveUserFromCategories,
    onReviewCategories,
    onApproveCategories,
    pendingCategories
  } = useApproveContractCategories({
    userData,
    setIsArchiveOpen,
    refreshUserData
  })

  const TABS = useMemo(() => {
    let total = totalNumber
    if (userData.role === ROLES.HOSPITAL_USER) {
      total =
        userData.hospitals?.reduce(
          (summ, hospital) => summ + hospital.contract_categories.length,
          0
        ) ?? 0
    }
    return PROFILE_TABS(
      id === 'new' || isPendingUserNeedsToReviewCategories,
      total
    )
  }, [
    id,
    isPendingUserNeedsToReviewCategories,
    totalNumber,
    userData.hospitals,
    userData.role
  ])
  const [activeTab, setActiveTab] = useState(TABS[0].key)

  const redirectAfterSubmit = useCallback(() => {
    if (redirectTab) {
      if (fromCommunity) {
        navigate(`${routes.myCommunity}/${redirectTab}`)
      } else {
        navigate(`${routes.settings}/${redirectTab}`)
      }
      return
    }
    if (fromCommunity) {
      navigate(`${routes.myCommunity}/${MY_COMMUNITY_KEYS.my_organization}`)
      return
    }
    navigate(-1)
  }, [fromCommunity, navigate, redirectTab])

  const isMyCommunityExternalProfile = fromCommunity || !isOwnProfile

  const canManageCCs =
    canCreateCommunityContractCategories ||
    canUpdateCommunityContractCategories ||
    canViewCommunityContractCategories

  const userCanSeeListOfCCViewModeOnly =
    (canViewFormContractCategoriesInput || canViewFormHospitalList) &&
    !canApprovePendingUsers &&
    !(isMyCommunityExternalProfile
      ? canManageCCs
      : canViewUserContractCategoriesSettings)

  const currentUserCanSeeListOfCC = isMyCommunityExternalProfile
    ? canManageCCs || userCanSeeListOfCCViewModeOnly
    : canViewUserContractCategories

  const isCCTabAvailable = canApprovePendingUsers || currentUserCanSeeListOfCC

  useEffect(() => {
    if (user?.community) {
      dispatch(fetchHealthSystemsList(user.community))
    } else if (user?.outside_community && user.health_system) {
      dispatch(
        fetchHealthSystemsList(user.health_system, { outsideCommunity: true })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.community, user.outside_community, user.health_system])

  const onSubmit = (values: TUserForm) => {
    const data: Object = {}
    for (const key in values) {
      if (key === 'hospitals') {
        if (
          !!values[key]?.filter((k) => !!Object.values(k).find((i) => !!i))
            ?.length
        )
          data[key] = values[key]?.filter(
            (k) => !!Object.values(k).find((i) => !!i)
          )
        else if (values[key] === null) data[key] = []
        else if (!values[key]?.length) data[key] = values[key]

        if (key === 'hospitals' && !!data['hospitals']) {
          data['hospitals']?.forEach((i) => delete i?.name)
        }
      } else {
        if (
          (typeof values[key] !== 'number' && !values[key]?.length) ||
          !values[key] ||
          (Array.isArray(values[key]) &&
            typeof values[key][0] === 'object' &&
            !Object.keys(values[key][0]).length)
        ) {
          if (values[key] == 0) {
            data[key] = values[key]
          }
        } else {
          data[key] = values[key]
        }
      }
    }
    if (id === user.uuid) {
      const payload = {
        first_name: data['first_name'],
        last_name: data['last_name'],
        phone_number: data['phone_number'] || '',
        avatar_id: data['avatar']
      }
      return updateUserProfile(payload)
        .then(() => redirectAfterSubmit())
        .catch((err) => {
          dispatch(setLoading(false))
          notification.error({ message: err?.data?.email[0] })
        })
    }
    if (userData.status === USER_STATUSES.PENDING && id) {
      const payload = {
        first_name: data['first_name'],
        last_name: data['last_name'],
        email: data['email'] || ''
      }
      return updateUser(id, payload)
        .then(() => redirectAfterSubmit())
        .finally(() => {
          dispatch(setLoading(false))
        })
    }
    if (id !== user.uuid && id && id !== 'new') {
      return updateUser(id, data)
        .then((res) => res)
        .then(() => redirectAfterSubmit())
        .catch((err) => {
          dispatch(setLoading(false))
          if (!!err?.data?.email?.length) {
            notification.error({ message: err?.data?.email[0] })
          }
          throw err
        })
        .finally(() => {
          dispatch(setLoading(false))
        })
    }
    if (id === 'new') {
      const createRequest = communityVendorId
        ? createVendorCommunityUser(communityVendorId, data)
        : createUser(data)

      return createRequest
        .then(() => {
          if (
            location.search.includes('&role=rca') ||
            location.search.includes('&role=community_hospital_user')
          ) {
            window.close()
          } else {
            redirectAfterSubmit()
          }
        })
        .catch((err) => {
          dispatch(setLoading(false))
          if (!!err?.data?.email?.length) {
            notification.error({ message: err?.data?.email[0] })
          }
          throw err
        })
    }
  }

  const userDataHospitals = useMemo(() => {
    return (
      userData.hospitals?.map(({ name, hospital }) => {
        return {
          name,
          uuid: String(hospital)
        }
      }) ?? []
    )
  }, [userData.hospitals])

  const renderUserContractCategories = useMemo(() => {
    if (!userData.uuid) return null
    if (userData.role === ROLES.HOSPITAL_USER) {
      return (
        <HospitalsTableContainer
          hospitals={userDataHospitals}
          userId={userData.uuid}
          canManageContractCategories
          updateCallback={getUserData}
          simpleViewModeOnly={userCanSeeListOfCCViewModeOnly}
        />
      )
    } else {
      return (
        <UserContractCategories
          role={profileUserRole}
          userId={userData.uuid}
          userName={`${userData.first_name} ${userData.last_name}`}
          simpleViewModeOnly={userCanSeeListOfCCViewModeOnly}
          canSeeExtendedViewMode={canSeeExtendedViewMode}
        />
      )
    }
  }, [
    userDataHospitals,
    userData,
    profileUserRole,
    getUserData,
    userCanSeeListOfCCViewModeOnly,
    canSeeExtendedViewMode
  ])

  const onArchiveUser = () => {
    setIsArchiveOpen(true)
  }

  return (
    <Container
      className="user-profile"
      breadcrumbs={USER_PROFILE_BREADCRUMBS(!!fromCommunity, id)}
      title={
        fromCommunity ? (
          <UserHeader user={userData} />
        ) : id && id !== 'new' ? (
          'User profile'
        ) : (
          'Create user profile'
        )
      }
      menu={
        isCCTabAvailable && (
          <Menu
            mode="horizontal"
            onSelect={(e) => setActiveTab(e.key)}
            selectedKeys={[activeTab]}
            defaultSelectedKeys={[activeTab]}
            items={TABS}
          />
        )
      }
    >
      {activeTab === TABS[0].key && (
        <>
          {isPendingUserNeedsToReviewCategories &&
            !isCurrentUserCanReviewCategories && (
              <Banner.Warning text="Some contract categories are awaiting approval." />
            )}
          {isCurrentUserCanReviewCategories && (
            <Banner.Warning
              className="user-profile_banner_warning"
              text={`${userData.first_name} ${userData.last_name} registered and selected ${pendingCategories.length} contract categories. Review and approve the categories to activate this account.`}
              button={[
                {
                  buttonText: 'Archive user',
                  onButtonClick: onArchiveUser,
                  buttonType: BUTTON_TYPES.DEFAULT
                },
                {
                  buttonText: 'Review categories',
                  onButtonClick: onReviewCategories,
                  buttonType: BUTTON_TYPES.PRIMARY
                }
              ]}
            />
          )}
          {isShow && (
            <Forms.User
              onSubmit={onSubmit}
              setIsActive={setIsActive}
              isActive={isActive}
              initialState={userData}
              hsHosUser={query?.hsHosUser}
              hsUser={query?.hsUser}
              form={userForm}
              fromCommunity={fromCommunity || userData.uuid !== user.uuid}
              isPendingUserNeedsToReviewCategories={
                isPendingUserNeedsToReviewCategories
              }
              vendorId={communityVendorId}
            />
          )}
          {isArchiveOpen && userData.uuid && (
            <UserArchivePopup<TSelectedUser>
              selected={{
                uuid: userData.uuid,
                first_name: userData.first_name || '',
                last_name: userData.last_name || ''
              }}
              isOpenFromTheOutside={isArchiveOpen}
              setIsOpenFromTheOutside={setIsArchiveOpen}
              isTable
              callback={refreshUserData}
            />
          )}
        </>
      )}
      {activeTab === TABS[1].key &&
        userData.uuid &&
        renderUserContractCategories}
      <ReviewAndApproveCategoriesPopup
        pendingCategories={pendingCategories}
        userName={`${userData.first_name} ${userData.last_name}`}
        onArchive={onArchiveUserFromCategories}
        onApproveCategories={onApproveCategories}
      />
      <ActivateWithoutCategoriesPopup
        userName={`${userData.first_name} ${userData.last_name}`}
        onApproveCategories={onApproveCategories}
      />
    </Container>
  )
}
